import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import clientCustomizationService from '../../../services/clientCustomizationService';

const RequestDetailsModal = ({ request, onClose, onStatusUpdate, onRefresh }) => {
  const [milestones, setMilestones] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadRequestDetails();
  }, [request?.id]);

  const loadRequestDetails = async () => {
    try {
      setLoading(true);
      const [milestonesData, activityData] = await Promise.all([
        clientCustomizationService?.getMilestones(request?.id),
        clientCustomizationService?.getActivityLog(request?.id)
      ]);
      setMilestones(milestonesData);
      setActivityLog(activityData);
    } catch (error) {
      console.error('Error loading request details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMilestoneStatusUpdate = async (milestoneId, newStatus) => {
    try {
      await clientCustomizationService?.updateMilestoneStatus(milestoneId, newStatus, 'admin@system.com');
      await loadRequestDetails();
      await onRefresh?.();
    } catch (error) {
      console.error('Error updating milestone:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{request?.requestTitle}</h2>
            <p className="text-sm text-muted-foreground mt-1">{request?.requestNumber} • {request?.companyName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-6">
          <div className="flex gap-6">
            {[
              { key: 'overview', label: 'Overview', icon: 'FileText' },
              { key: 'milestones', label: 'Milestones', icon: 'Target' },
              { key: 'activity', label: 'Activity Log', icon: 'Activity' }
            ]?.map(tab => (
              <button
                key={tab?.key}
                onClick={() => setActiveTab(tab?.key)}
                className={`py-3 px-4 border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === tab?.key
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Description</h3>
                <p className="text-muted-foreground">{request?.requestDescription}</p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Request Details</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Type</p>
                      <p className="text-foreground font-medium">
                        {request?.customizationType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Complexity</p>
                      <p className="text-foreground font-medium capitalize">{request?.customizationComplexity}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Priority</p>
                      <p className="text-foreground font-medium capitalize">{request?.priority}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p className="text-foreground font-medium">
                        {request?.requestStatus?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Cost & Timeline</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Estimated Cost</p>
                      <p className="text-foreground font-medium">K{(request?.estimatedCostKina || 0)?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Estimated Hours</p>
                      <p className="text-foreground font-medium">{request?.estimatedHours || 0} hours</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Target Completion</p>
                      <p className="text-foreground font-medium">{formatDate(request?.targetCompletionDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Progress</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-primary h-full transition-all"
                            style={{ width: `${request?.developmentProgressPercentage || 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-foreground">
                          {Math.round(request?.developmentProgressPercentage || 0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Assignment</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Assigned Developer</p>
                      <p className="text-foreground font-medium">{request?.assignedDeveloper || 'Unassigned'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Assigned Team</p>
                      <p className="text-foreground font-medium">{request?.assignedTeam || 'Unassigned'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Current Milestone</p>
                      <p className="text-foreground font-medium">{request?.currentMilestone || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Client Contact</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Contact Name</p>
                      <p className="text-foreground font-medium">{request?.clientContactName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Contact Email</p>
                      <p className="text-foreground font-medium">{request?.clientContactEmail || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Contact Phone</p>
                      <p className="text-foreground font-medium">{request?.clientContactPhone || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'milestones' && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Development Milestones</h3>
              {loading ? (
                <div className="text-center py-8">
                  <Icon name="Loader2" className="animate-spin h-6 w-6 mx-auto text-primary" />
                </div>
              ) : milestones?.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No milestones defined</p>
              ) : (
                <div className="space-y-4">
                  {milestones?.map((milestone, index) => (
                    <div key={milestone?.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Step {milestone?.milestoneOrder}</span>
                            <h4 className="text-foreground font-semibold">{milestone?.milestoneName}</h4>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">{milestone?.milestoneDescription}</p>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Estimated Hours</p>
                              <p className="text-foreground font-medium">{milestone?.estimatedHours || 0} hours</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Target Date</p>
                              <p className="text-foreground font-medium">{formatDate(milestone?.targetDate)}</p>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            milestone?.milestoneStatus === 'completed'
                              ? 'bg-green-500/10 text-green-600'
                              : milestone?.milestoneStatus === 'in_progress' ?'bg-amber-500/10 text-amber-600' :'bg-gray-500/10 text-gray-600'
                          }`}>
                            {milestone?.milestoneStatus?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Activity Timeline</h3>
              {loading ? (
                <div className="text-center py-8">
                  <Icon name="Loader2" className="animate-spin h-6 w-6 mx-auto text-primary" />
                </div>
              ) : activityLog?.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No activity recorded</p>
              ) : (
                <div className="space-y-4">
                  {activityLog?.map((activity) => (
                    <div key={activity?.id} className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="Activity" size={16} className="text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-foreground font-medium">{activity?.activityDescription}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          <span>{activity?.performedBy}</span>
                          <span>•</span>
                          <span>{formatDateTime(activity?.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailsModal;